import { FormState } from '@app/lib/form/contracts/FormState';

import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { useTranslations } from 'next-intl';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { Heading } from '@app/app/_components/Heading/Heading';
import { useCallback, useState, ChangeEvent } from 'react';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { Button } from '@app/app/_components/Button/Button';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { Address } from '@app/domain/model/Address';
import { PolicyholderFormFields } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePolicyholderFormFields';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { PolicyholderStep } from '@app/app/esales/personal-accidents/_sections/steps/PolicyholderStep/PolicyholderStep';
import { formatAddress } from '@app/lib/address-formatter/formatAddress';

interface Props {
    policyholderFields: PolicyholderFormFields;
    formState: FormState<PurchasePersonalInsuranceResult>;
    personalCode: string;
    onSave: (data: PolicyholderFormFields) => void;
}

export function PolicyholderSummaryCard({ policyholderFields, formState, personalCode, onSave }: Props) {
    const t = useTranslations();
    const [sideSheetOpen, setSideSheetOpen] = useState(false);
    const [editFormData, setEditFormData] = useState<PolicyholderFormFields>(policyholderFields);

    const handleFirstNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, firstName: e.target.value }));
    }, []);

    const handleLastNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, lastName: e.target.value }));
    }, []);

    const handleAddressChange = useCallback((address: Nullable<Address>) => {
        setEditFormData(prev => ({ ...prev, address }));
    }, []);

    const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, phoneNumber: e.target.value }));
    }, []);

    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, email: e.target.value }));
    }, []);

    const handleEmailConfirmationChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, emailConfirmation: e.target.value }));
    }, []);

    const handleIsInsuredPersonChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, isInsuredPerson: e.target.checked }));
    }, []);

    const editHandlers = {
        handleFirstNameChange,
        handleLastNameChange,
        handleAddressChange,
        handlePhoneNumberChange,
        handleEmailChange,
        handleEmailConfirmationChange,
        handleIsInsuredPersonChange,
    };

    const handleOpenSideSheet = useCallback(() => {
        setEditFormData({
            ...policyholderFields,
            address: policyholderFields.address ? { ...policyholderFields.address } : null,
        });
        setSideSheetOpen(true);
    }, [policyholderFields]);

    const handleCloseSideSheet = () => {
        setSideSheetOpen(false);
    };

    const handleCancel = () => {
        handleCloseSideSheet();
    };

    const handleSave = () => {
        onSave(editFormData);
        handleCloseSideSheet();
    };

    const sideSheetHeader = (
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <div className="flex items-center gap-3">
                <button className="cursor-pointer" onClick={handleCloseSideSheet}>
                    <ArrowLeftIcon className="h-5 w-5 text-neutral-500" />
                </button>
                <Heading level="h3">{t('eshop.personalAccidents.editPolicyholderInformation')}</Heading>
            </div>
        </div>
    );

    const sideSheetFooter = (
        <div className="flex h-min w-full items-center justify-between gap-6 bg-white px-6 py-4 shadow-2xl">
            <Button
                variant="outlined"
                className="w-full"
                rightAddon={<CloseIcon className="size-4" />}
                onClick={handleCancel}>
                {t('general.cancel')}
            </Button>
            <Button className="w-full" rightAddon={<CheckThinIcon className="size-5" />} onClick={handleSave}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <>
            <CollapsablePanel
                label={`${policyholderFields.firstName} ${policyholderFields.lastName}`}
                collapsable={false}
                addon={<TextButton onClick={handleOpenSideSheet}>{t('general.edit')}</TextButton>}>
                <div className="p-6">
                    <div className="flex flex-col gap-2 border-b border-neutral-150 pb-4">
                        <Heading level="h4">{t('eshop.personalAccidents.summaryStep.personalInformation')}</Heading>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            <ReadOnlyField label={t('eshop.personalAccidents.summaryStep.name')}>
                                {policyholderFields.firstName}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.personalAccidents.summaryStep.surname')}>
                                {policyholderFields.lastName}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.personalAccidents.summaryStep.personalCode')}>
                                {personalCode}
                            </ReadOnlyField>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 border-b border-neutral-150 py-4">
                        <Heading level="h4">{t('eshop.personalAccidents.summaryStep.contactInformation')}</Heading>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <ReadOnlyField
                                className="sm:col-span-2"
                                label={t('eshop.personalAccidents.summaryStep.addressLabel')}>
                                {policyholderFields.address ? formatAddress(policyholderFields.address) : ''}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.personalAccidents.summaryStep.phone')}>
                                {policyholderFields.phoneNumber ? `+370${policyholderFields.phoneNumber}` : ''}
                            </ReadOnlyField>
                            <ReadOnlyField
                                className="max-sm:break-words sm:text-nowrap"
                                label={t('eshop.personalAccidents.summaryStep.email')}>
                                {policyholderFields.email}
                            </ReadOnlyField>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 pt-4">
                        <Heading level="h4">
                            {t('eshop.personalAccidents.summaryStep.insurerOfPersonalAccidentsPolicy')}
                        </Heading>
                        <ReadOnlyField label={t('eshop.personalAccidents.summaryStep.insurerOfPolicy')}>
                            {policyholderFields.isInsuredPerson
                                ? t('eshop.personalAccidents.summaryStep.insuredIsInsurer')
                                : t('eshop.personalAccidents.summaryStep.insuredIsNotInsurer')}
                        </ReadOnlyField>
                    </div>
                </div>
            </CollapsablePanel>

            <SideSheet open={sideSheetOpen} header={sideSheetHeader} footer={sideSheetFooter} contentClassName="p-6">
                {sideSheetOpen && (
                    <div className="flex flex-col gap-6">
                        <PolicyholderStep
                            policyholderFields={editFormData}
                            policyholderHandlers={editHandlers}
                            formState={formState}
                            personalCode={personalCode}
                            disabledCheckbox={true}
                        />
                    </div>
                )}
            </SideSheet>
        </>
    );
}
